import React, { useState, useRef, useCallback, useEffect } from "react";
import * as Survey from "survey-core";
import {
  ReactQuestionFactory,
  SurveyQuestionElementBase,
} from "survey-react-ui";
import {
  AddressSearchService,
  AutocompleteSuggestion,
} from "../../../landers/Utils/services/AddressSearch.service";
import { fieldValidation } from "../Validators";

const CUSTOM_TYPE = "addressautocomplete";

interface AddressValue {
  line_1: string;
  line_2: string;
  line_3: string;
  post_town: string;
  postcode: string;
  county: string;
  country: string;
}

const EMPTY_ADDRESS: AddressValue = {
  line_1: "",
  line_2: "",
  line_3: "",
  post_town: "",
  postcode: "",
  county: "",
  country: "",
};

function getSurvey(question: Survey.Question): Survey.SurveyModel | null {
  return question.survey as Survey.SurveyModel | null;
}

function formatAddressString(addr: AddressValue): string {
  return [addr.line_1, addr.line_2, addr.line_3].filter(Boolean).join(", ");
}

function normalizePostcodeForValidation(postcode: string): string {
  const compact = postcode.replace(/\s+/g, "").toUpperCase();
  if (compact.length <= 3) return compact;
  return `${compact.slice(0, -3)} ${compact.slice(-3)}`;
}

function hasAddressValue(addr: AddressValue): boolean {
  return Object.values(addr).some((value) => value.trim() !== "");
}

function getAddressFromQuestion(question: Survey.Question): AddressValue {
  const stored = (
    question as QuestionAddressAutocompleteModel
  ).addressValue as AddressValue | null;
  if (stored) return stored;

  const survey = getSurvey(question);
  return {
    line_1: (question.value as string) || "",
    line_2: "",
    line_3: "",
    post_town: (survey?.getValue("city") as string) || "",
    postcode: (survey?.getValue("postcode") as string) || "",
    county: (survey?.getValue("region") as string) || "",
    country: (survey?.getValue("country") as string) || "",
  };
}

function setSiblingFields(survey: Survey.SurveyModel | null, addr: AddressValue) {
  if (!survey) return;
  survey.setValue("city", addr.post_town);
  survey.setValue("region", addr.county);
  survey.setValue("country", addr.country);
  survey.setValue("postcode", addr.postcode);
}

function initSelectedAddress(question: Survey.Question): AddressValue | null {
  const val = question.value as string | undefined;
  if (!val) return null;
  return getAddressFromQuestion(question);
}

export class QuestionAddressAutocompleteModel extends Survey.Question {
  getType() {
    return CUSTOM_TYPE;
  }

  get readOnly() {
    return (
      this.getPropertyValue("readOnly") || this.getPropertyValue("readonly")
    );
  }
  set readOnly(val: boolean) {
    this.setPropertyValue("readOnly", val);
  }

  get addressValue(): AddressValue | null {
    return this.getPropertyValue("addressValue", null);
  }
  set addressValue(val: AddressValue | null) {
    this.setPropertyValue("addressValue", val);
  }

  validate(): boolean {
    this.clearErrors();
    const addr = getAddressFromQuestion(this);
    const hasAnyAddress = hasAddressValue(addr);

    if (!this.isRequired && !hasAnyAddress) {
      return true;
    }

    if (!addr.line_1.trim()) {
      this.errors.push(
        new Survey.CustomError("Please enter the first line of your address")
      );
      return false;
    }

    if (!addr.postcode.trim()) {
      this.errors.push(new Survey.CustomError("Please enter your postcode"));
      return false;
    }

    if (
      !fieldValidation(
        normalizePostcodeForValidation(addr.postcode),
        "postcode",
        undefined,
        undefined,
        true
      )
    ) {
      this.errors.push(new Survey.CustomError("Please enter a valid UK postcode"));
      return false;
    }

    return true;
  }
}

Survey.Serializer.addClass(
  CUSTOM_TYPE,
  [],
  () => new QuestionAddressAutocompleteModel(""),
  "question"
);

Survey.ElementFactory.Instance.registerElement(CUSTOM_TYPE, (name) => {
  return new QuestionAddressAutocompleteModel(name);
});

function AddressAutocompleteQuestion(props: {
  question: QuestionAddressAutocompleteModel;
}) {
  const { question } = props;

  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState<AutocompleteSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showSuggestionsAbove, setShowSuggestionsAbove] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<AddressValue | null>(
    () => initSelectedAddress(question)
  );
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [isSearchVisible, setIsSearchVisible] = useState(
    () => !initSelectedAddress(question)
  );
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchWrapperRef = useRef<HTMLDivElement>(null);
  const latestSearchQueryRef = useRef("");
  const searchRequestIdRef = useRef(0);
  const skipNextValueSyncRef = useRef(false);
  const hasStartedSearchRef = useRef(false);
  const originalTitleLocationRef = useRef(question.titleLocation || "default");

  const isReadOnly = question.readOnly || question.isReadOnly;
  const hasError = question.errors.length > 0;
  const suggestionsListId = `${question.name || "address"}-suggestions`;

  useEffect(() => {
    const shouldHideTitle = selectedAddress && !isSearchVisible;
    question.titleLocation = shouldHideTitle
      ? "hidden"
      : originalTitleLocationRef.current;

    return () => {
      question.titleLocation = originalTitleLocationRef.current;
    };
  }, [question, selectedAddress, isSearchVisible]);

  useEffect(() => {
    question.addressValue = initSelectedAddress(question);
  }, [question]);

  // A postcode already known for this visitor (e.g. from a resubmission token) starts the address search until they
  // type. Read on each render: a restored draft is assigned after this field mounts and raises no value change.
  const knownPostcode = String(getSurvey(question)?.getValue("postcode") ?? "").trim();
  useEffect(() => {
    if (hasStartedSearchRef.current || selectedAddress || !knownPostcode) return;
    hasStartedSearchRef.current = true;
    latestSearchQueryRef.current = knownPostcode;
    setSearchQuery(knownPostcode);
  }, [knownPostcode, selectedAddress]);

  // Sync from external value changes (e.g. prefill via survey.data)
  useEffect(() => {
    const handler = () => {
      if (skipNextValueSyncRef.current) {
        skipNextValueSyncRef.current = false;
        return;
      }

      const val = question.value as string | undefined;
      if (val) {
        const nextAddress = initSelectedAddress(question);
        question.addressValue = nextAddress;
        setSelectedAddress(nextAddress);
        setIsManualEntry(false);
        setIsSearchVisible(false);
        setSearchQuery("");
        setSuggestions([]);
      } else {
        question.addressValue = null;
        setSelectedAddress(null);
        setIsManualEntry(false);
        setIsSearchVisible(true);
      }
    };
    question.registerFunctionOnPropertyValueChanged("value", handler, "addr-sync");
    return () => {
      question.unRegisterFunctionOnPropertyValueChanged("value", "addr-sync");
    };
  }, [question]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        searchWrapperRef.current &&
        !searchWrapperRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const updateSuggestionsPlacement = useCallback(() => {
    const rect = searchWrapperRef.current?.getBoundingClientRect();
    if (!rect) return;

    const dropdownHeight = 288;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    setShowSuggestionsAbove(spaceBelow < dropdownHeight && spaceAbove > spaceBelow);
  }, []);

  const fetchSuggestions = useCallback(async (query: string) => {
    const trimmedQuery = query.trim();
    if (trimmedQuery.length <= 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const requestId = searchRequestIdRef.current + 1;
    searchRequestIdRef.current = requestId;
    setIsLoading(true);

    try {
      const results = await AddressSearchService.getAddressAutocomplete(trimmedQuery);
      if (
        requestId !== searchRequestIdRef.current ||
        latestSearchQueryRef.current.trim() !== trimmedQuery
      ) {
        return;
      }

      setSuggestions(results);
      if (results.length > 0) updateSuggestionsPlacement();
      setShowSuggestions(results.length > 0);
      setHighlightedIndex(-1);
    } catch {
      if (requestId === searchRequestIdRef.current) {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } finally {
      if (requestId === searchRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [updateSuggestionsPlacement]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    hasStartedSearchRef.current = true;
    const previousValue = latestSearchQueryRef.current;
    const isDeleting = value.length < previousValue.length;

    latestSearchQueryRef.current = value;
    setSearchQuery(value);
    if (!selectedAddress) {
      skipNextValueSyncRef.current = true;
      question.value = value;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (isDeleting || value.trim().length <= 2) {
      searchRequestIdRef.current += 1;
      setIsLoading(false);
      setSuggestions([]);
      setShowSuggestions(false);
      setHighlightedIndex(-1);
      return;
    }

    debounceRef.current = setTimeout(() => fetchSuggestions(value), 300);
  };

  const handleManualEntry = () => {
    if (!selectedAddress) {
      const enteredAddress = searchQuery.trim();
      // The search text becomes line 1; a postcode already known (e.g. from a token) is kept rather than cleared.
      const knownManualPostcode = String(getSurvey(question)?.getValue("postcode") ?? "").trim();
      const manualAddress = {
        ...EMPTY_ADDRESS,
        line_1: enteredAddress === knownManualPostcode ? "" : enteredAddress,
        postcode: knownManualPostcode,
      };
      setSelectedAddress(manualAddress);
      question.addressValue = manualAddress;
      skipNextValueSyncRef.current = true;
      question.value = formatAddressString(manualAddress);
      setSiblingFields(getSurvey(question), manualAddress);
    }

    setIsSearchVisible(false);
    setSearchQuery("");
    latestSearchQueryRef.current = "";
    setSuggestions([]);
    setShowSuggestions(false);
    setIsManualEntry(true);
  };

  const applyAddress = (addr: AddressValue) => {
    setSelectedAddress(addr);
    setIsSearchVisible(false);
    setSearchQuery("");
    latestSearchQueryRef.current = "";
    question.addressValue = addr;
    skipNextValueSyncRef.current = true;
    question.value = formatAddressString(addr);
    setSiblingFields(getSurvey(question), addr);
  };

  const handleClearAddress = () => {
    setSelectedAddress(null);
    setIsManualEntry(false);
    setIsSearchVisible(true);
    setSearchQuery("");
    latestSearchQueryRef.current = "";
    searchRequestIdRef.current += 1;
    setIsLoading(false);
    setSuggestions([]);
    setShowSuggestions(false);
    setHighlightedIndex(-1);
    question.addressValue = null;
    skipNextValueSyncRef.current = true;
    question.value = "";
    setSiblingFields(getSurvey(question), EMPTY_ADDRESS);
  };

  const handleEditAddressSearch = () => {
    setIsSearchVisible(true);
    setIsManualEntry(false);
    setSearchQuery("");
    latestSearchQueryRef.current = "";
    searchRequestIdRef.current += 1;
    setIsLoading(false);
    setSuggestions([]);
    setShowSuggestions(false);
    setHighlightedIndex(-1);
  };

  const handleSelect = async (suggestion: AutocompleteSuggestion) => {
    setShowSuggestions(false);
    setIsLoading(true);
    try {
      const resolved = await AddressSearchService.resolveAddress(suggestion);
      if (!resolved) return;

      applyAddress({
        line_1: resolved?.line_1 || "",
        line_2: resolved?.line_2 || "",
        line_3: resolved?.line_3 || "",
        post_town: resolved?.post_town || "",
        postcode: resolved?.postcode || "",
        county: resolved?.county || "",
        country: (resolved as any).country || "United Kingdom",
      });
    } catch {
      console.error("Failed to resolve address");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFieldChange =
    (field: keyof AddressValue) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const updated = { ...(selectedAddress || EMPTY_ADDRESS), [field]: e.target.value };
      setSelectedAddress(updated);
      question.addressValue = updated;
      skipNextValueSyncRef.current = true;
      question.value = formatAddressString(updated);
      setSiblingFields(getSurvey(question), updated);
    };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter" && highlightedIndex >= 0) {
      e.preventDefault();
      handleSelect(suggestions[highlightedIndex]);
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const inputClassName = `sd-input sd-text ${hasError ? "sd-input--error" : ""} ${isReadOnly ? "sd-input--readonly" : ""
    }`;

  const previewLines = selectedAddress
    ? [
      selectedAddress.line_1,
      selectedAddress.line_2,
      selectedAddress.line_3,
      selectedAddress.post_town,
      selectedAddress.county,
      selectedAddress.country,
      selectedAddress.postcode,
    ].filter(Boolean)
    : [];

  // ── Address fields shown after selection ──
  const fields: { key: keyof AddressValue; label: string }[] = [
    { key: "line_1", label: "Address" },
    { key: "line_2", label: "Address Line 2" },
    { key: "line_3", label: "Address Line 3" },
    { key: "post_town", label: "City" },
    { key: "county", label: "Region" },
    { key: "country", label: "Country" },
    { key: "postcode", label: "Postcode" },
  ];

  // ── Search input ──
  return (
    <div className="sd-question__content" ref={containerRef}>
      {isSearchVisible && (
        <div className="position-relative mb-3" ref={searchWrapperRef}>
          <input
            type="text"
            className={inputClassName}
            placeholder={
              selectedAddress
                ? "Search for a different address..."
                : "Start typing your address..."
            }
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
            autoComplete="street-address"
            name="street-address"
            onFocus={() => {
              // A prefilled postcode hasn't been searched yet: look it up when the visitor comes to the field.
              if (suggestions.length === 0 && !selectedAddress && searchQuery.trim().length > 2) {
                void fetchSuggestions(searchQuery);
              }
              if (suggestions.length > 0) {
                updateSuggestionsPlacement();
                setShowSuggestions(true);
              }
            }}
            readOnly={isReadOnly}
            aria-label="Address search"
            aria-controls={suggestionsListId}
            aria-expanded={showSuggestions ? "true" : "false"}
            aria-autocomplete="list"
            role="combobox"
          />

          {isLoading && (
            <div
              className="position-absolute end-0 top-50 translate-middle-y pe-3"
              style={{ pointerEvents: "none" }}
            >
              <span
                className="spinner-border spinner-border-sm text-secondary"
                role="status"
              />
            </div>
          )}

          {showSuggestions && (
            <div
              className={`sv-popup__container bg-white ${showSuggestionsAbove ? "mb-2" : "mt-2"
                }`}
              style={{
                position: "absolute",
                left: "50%",
                top: showSuggestionsAbove ? "auto" : "100%",
                bottom: showSuggestionsAbove ? "100%" : "auto",
                transform: "translateX(-50%)",
                width: "calc(100% - 8px)",
                zIndex: 1000,
                borderRadius: "16px",
                border: "1px solid rgba(0, 0, 0, 0.25)",
                boxShadow: "0 12px 28px rgba(15, 23, 42, 0.25)",
              }}
            >
              <div className="sv-popup__body-content py-4 ps-4">
                <div
                  className="sv-popup__scrolling-content"
                  style={{ maxHeight: 240, overflowY: "auto" }}
                >
                  <ul
                    id={suggestionsListId}
                    className="sv-single-select-list m-0 p-0"
                    role="listbox"
                  >
                    {suggestions.map((s, i) => (
                      <li
                        key={(s as any).id || i}
                        role="option"
                        aria-selected={i === highlightedIndex}
                        className={`sv-list__item${i === highlightedIndex ? " sv-list__item--focused bg-gray-200" : ""
                          }`}
                        style={{ whiteSpace: "normal", borderRadius: "16px" }}
                        onMouseEnter={() => setHighlightedIndex(i)}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSelect(s);
                        }}
                      >
                        <div
                          className="sv-list__item-body"
                          style={{ minWidth: 0, whiteSpace: "normal" }}
                        >
                          <span
                            style={{
                              display: "block",
                              overflowWrap: "anywhere",
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                            }}
                          >
                            {(s as any).suggestion}
                          </span>

                        </div>
                        <div className="bg-gray-300 w-90 justify-self-center" style={{ height: "1px", justifySelf: "center", width: "95%" }}></div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {!selectedAddress && !isReadOnly && isSearchVisible && (
        <button
          type="button"
          className="btn btn-sm btn-secondary align-self-start mb-3"
          onClick={handleManualEntry}
        >
          Enter manually
        </button>
      )}

      {selectedAddress && !isManualEntry && (
        <div className="d-flex flex-column gap-2">
          <div className="d-flex justify-between"></div>
          <div className="d-flex align-items-center justify-content-between gap-2 mt-2">
            <h5 className="mb-0">Selected Address:</h5>
            {!isReadOnly && !isSearchVisible && (
              <button
                type="button"
                className="btn btn-sm btn-primary text-white"
                onClick={handleEditAddressSearch}
              >
                Edit address
              </button>
            )}
          </div>
          <div className="d-flex align-items-start justify-content-between gap-3">
            <div className="text-black fs-4">
              {previewLines.map((line, index) => (
                <div key={`${line}-${index}`}>{line}</div>
              ))}
            </div>
          </div>
          {!isReadOnly && (
            <div className="d-flex align-items-center justify-content-between gap-2 mt-2 mt-md-3">
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={handleManualEntry}
              >
                Enter manually
              </button>
              <button
                type="button"
                className="btn btn-sm btn-icon btn-outline btn-outline-danger btn-active-light-danger"
                onClick={handleClearAddress}
                aria-label="Clear address"
                title="Clear address"
              >
                <i className="bi bi-trash3" aria-hidden="true"></i>
              </button>
            </div>
          )}
        </div>
      )}

      {selectedAddress && isManualEntry && (
        <div className="d-flex flex-column gap-2 gap-md-3">
          {fields.map(({ key, label }) => (
            <div key={key} className="d-flex flex-column gap-1">
              <label className="fs-6 fs-md-5 fs-lg-4 text-gray-700">{label}</label>
              <input
                type="text"
                className={inputClassName}
                value={selectedAddress[key]}
                onChange={handleFieldChange(key)}
                readOnly={isReadOnly}
                autoComplete={
                  key === "line_1"
                    ? "address-line1"
                    : key === "line_2"
                    ? "address-line2"
                    : key === "line_3"
                    ? "address-line3"
                    : key === "post_town"
                    ? "address-level2"
                    : key === "county"
                    ? "address-level1"
                    : key === "country"
                    ? "country-name"
                    : key === "postcode"
                    ? "postal-code"
                    : undefined
                }
              />
            </div>
          ))}
          {!isReadOnly && (
            <button
              type="button"
              className="btn btn-sm btn-secondary align-self-start mt-1"
              onClick={() => setIsManualEntry(false)}
            >
              Collapse address
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// SurveyJS requires a class component for registration
class SurveyQuestionAddressAutocomplete extends SurveyQuestionElementBase {
  get question() {
    return this.questionBase as QuestionAddressAutocompleteModel;
  }

  renderElement() {
    return <AddressAutocompleteQuestion question={this.question} />;
  }
}

ReactQuestionFactory.Instance.registerQuestion(CUSTOM_TYPE, (props) => {
  return React.createElement(SurveyQuestionAddressAutocomplete, props);
});
